import React from 'react'

import { FlexPortionTileBadge, FlexPortionTileEditor } from '@features/recipe-tile'
import type { CompactCampaignBannerHeaderContentBlock } from '@library/api-menu-experience'
import { DeviceType, useDeviceType } from '@library/dom'
import { CompactCampaignBanner } from '@library/menu-page'
import type { RecipeUUID } from '@library/menu-service'

import { useMerchandisingBannerBasket } from 'routes/Menu/MenuExperience/hooks/useMerchandisingBannerBasket'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'

import { useMenuExperienceCategoryNavigation } from '../menuExperienceNavigationContext'
import { useMenuExperienceRecipeDetails } from '../menuExperienceRecipeDetailContext'
import { useRecipeGridFlex } from './useRecipeGridFlex'

type CompactCampaignBannerContentBlockWrapperProps = {
  contentBlock: CompactCampaignBannerHeaderContentBlock
}

export const CompactCampaignBannerContentBlockWrapper = ({
  contentBlock,
}: CompactCampaignBannerContentBlockWrapperProps) => {
  const { selectCategory } = useMenuExperienceCategoryNavigation()
  const { openRecipeDetail } = useMenuExperienceRecipeDetails()
  const isMobile = useDeviceType() === DeviceType.MOBILE
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const { canAddRecipes, getRecipeCardState, onAddClick, onRemoveClick, reachedLimit } =
    useMerchandisingBannerBasket(contentBlock)
  const {
    closeFlexPortionEditor,
    getFlexPortionData,
    openFlexPortionEditor,
    removeFlexRecipe,
    selectFlexPortions,
    trackFlexPortionEdit,
    updateFlexPortions,
  } = useRecipeGridFlex({
    items: contentBlock.items,
    removalPosition: 'collection_merchandising_header',
  })

  const onCtaClick = (targetCategory: { id: string; slug: string }) => {
    selectCategory(targetCategory, 'merch_banner', undefined, {
      merch_header_type: contentBlock.type,
    })
  }

  const onRecipeClick = (recipeId: RecipeUUID, initialFlexPortionEditorOpen = false) => {
    const recipe = contentBlock.items.find((item) => item.id === recipeId)

    if (!recipe) {
      return
    }

    openRecipeDetail({
      recipeId,
      title: recipe.title,
      source: 'merch_banner',
      merchHeaderType: contentBlock.type,
      initialFlexPortionEditorOpen,
    })
  }

  const onEditFlexPortions = (recipeId: RecipeUUID) => {
    if (isMobile) {
      openFlexPortionEditor(recipeId)

      return
    }

    trackFlexPortionEdit(recipeId)
    onRecipeClick(recipeId, true)
  }

  const renderFlexPortionBadge = (recipeId: RecipeUUID) => {
    const { isVisible, quantity, numPortions } = getFlexPortionData(recipeId)

    if (!isVisible) {
      return null
    }

    return (
      <FlexPortionTileBadge
        recipeCount={quantity}
        portionCount={numPortions}
        onEdit={() => onEditFlexPortions(recipeId)}
      />
    )
  }

  const renderFlexPortionEditor = (recipeId: RecipeUUID) => {
    const { isEditorOpen, quantity, numPortions, portions } = getFlexPortionData(recipeId)

    if (!isEditorOpen) {
      return null
    }

    return (
      <FlexPortionTileEditor
        isOpen
        recipeCount={quantity}
        recipeId={recipeId}
        numPortions={numPortions}
        portions={portions}
        onClose={closeFlexPortionEditor}
        onRemoveRecipe={removeFlexRecipe}
        onUpdateRecipePortions={updateFlexPortions}
        onSelectPortions={(updatedNumPortions) => selectFlexPortions(recipeId, updatedNumPortions)}
      />
    )
  }

  return (
    <CompactCampaignBanner
      contentBlock={contentBlock}
      getRecipeCardState={getRecipeCardState}
      canAddRecipes={canAddRecipes}
      reachedLimit={reachedLimit}
      onAddClick={onAddClick}
      onBlockedAddAttempt={setBrowserCTAVisible}
      onCtaClick={onCtaClick}
      onRecipeClick={onRecipeClick}
      onRemoveClick={onRemoveClick}
      renderFlexPortionBadge={renderFlexPortionBadge}
      renderFlexPortionEditor={renderFlexPortionEditor}
    />
  )
}
